import React from 'react';
import {
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBook,
  FaChartLine,
  FaMoneyBillWave,
  FaStar,
  FaCheck,
  FaSpinner,
  FaEnvelope,
} from 'react-icons/fa';
import { formatCourseFee } from '../../../config/constants/creativeSamurai';

const StatCard = ({ icon, label, value, accent = 'text-designer-primary', bg = 'bg-red-50' }) => (
  <div className="bg-white rounded-xl border border-gray-200/80 p-4 flex items-start gap-3">
    <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
      <span className={`text-sm ${accent}`}>{icon}</span>
    </div>
    <div className="min-w-0">
      <p className="text-xl font-bold text-designer-dark tabular-nums leading-tight">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
  </div>
);

const HighlightBanner = ({ children, gradient }) => (
  <div className={`rounded-xl ${gradient} p-4 sm:p-5 mb-4 border border-gray-200/40`}>
    {children}
  </div>
);

const ProgressBar = ({ pct, label, barColor = 'bg-green-500', trackColor = 'bg-white/60' }) => (
  <div className="flex items-center gap-3 w-full sm:w-auto sm:min-w-[200px]">
    <div className={`flex-1 h-2 ${trackColor} rounded-full overflow-hidden`}>
      <div
        className={`h-full ${barColor} rounded-full transition-all duration-700`}
        style={{ width: `${pct}%` }}
      />
    </div>
    <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">{label}</span>
  </div>
);

export const EnrollmentStatsPanel = ({ stats }) => {
  const total = (stats.paid_count || 0) + (stats.unpaid_count || 0);
  const paidPct = total > 0 ? Math.round((stats.paid_count / total) * 100) : 0;

  return (
    <div className="mb-5">
      <HighlightBanner gradient="bg-gradient-to-r from-emerald-50 via-white to-red-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <FaMoneyBillWave className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700 tabular-nums">
                {formatCourseFee(stats.total_income ?? 0)}
              </p>
              <p className="text-xs text-gray-500">Total income collected</p>
            </div>
          </div>
          {(stats.total_discount_given ?? 0) > 0 && (
            <span className="text-xs font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200/60 tabular-nums self-start">
              -{formatCourseFee(stats.total_discount_given)} in discounts
            </span>
          )}
          <div className="sm:ml-auto w-full sm:w-auto">
            <ProgressBar pct={paidPct} label={`${paidPct}% fully paid`} />
          </div>
        </div>
      </HighlightBanner>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard icon={<FaUsers />} label="Total Enrolled" value={stats.total_enrollments} />
        <StatCard icon={<FaCheckCircle />} label="Fully Paid" value={stats.paid_count} accent="text-green-600" bg="bg-green-50" />
        <StatCard icon={<FaClock />} label="Partial" value={stats.partial_count ?? 0} accent="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={<FaTimesCircle />} label="Unpaid" value={stats.unpaid_count} accent="text-amber-500" bg="bg-amber-50" />
        <StatCard icon={<FaBook />} label="Last 7 Days" value={stats.last_7_days} accent="text-blue-500" bg="bg-blue-50" />
        <StatCard icon={<FaChartLine />} label="Last 30 Days" value={stats.last_30_days} accent="text-purple-500" bg="bg-purple-50" />
      </div>
    </div>
  );
};

export const ReviewStatsPanel = ({ stats }) => {
  const total = Number(stats.total_reviews) || 0;
  const approved = Number(stats.approved_reviews) || 0;
  const approvedPct = total > 0 ? Math.round((approved / total) * 100) : 0;
  const avgRating = stats.average_rating ? Number(stats.average_rating) : null;

  return (
    <div className="mb-5">
      <HighlightBanner gradient="bg-gradient-to-r from-yellow-50 via-white to-green-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <FaStar className="text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-designer-dark tabular-nums">
                {avgRating ? `${avgRating}` : '-'}
                {avgRating && <span className="text-yellow-400 text-lg ml-1">★</span>}
              </p>
              <p className="text-xs text-gray-500">Average rating</p>
            </div>
          </div>
          <div className="sm:ml-auto w-full sm:w-auto">
            <ProgressBar
              pct={approvedPct}
              label={`${approvedPct}% approved`}
              barColor="bg-green-500"
              trackColor="bg-yellow-100"
            />
          </div>
        </div>
      </HighlightBanner>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<FaChartLine />} label="Total Reviews" value={stats.total_reviews} />
        <StatCard icon={<FaCheck />} label="Approved" value={stats.approved_reviews} accent="text-green-600" bg="bg-green-50" />
        <StatCard icon={<FaSpinner />} label="Pending" value={stats.pending_reviews} accent="text-yellow-500" bg="bg-yellow-50" />
        <StatCard
          icon={<FaStar />}
          label="Avg Rating"
          value={avgRating ? `${avgRating} ★` : '-'}
          accent="text-yellow-500"
          bg="bg-yellow-50"
        />
      </div>
    </div>
  );
};

export const ContactStatsPanel = ({ stats }) => {
  const total = Number(stats.total_messages) || 0;
  const read = Number(stats.read_messages) || 0;
  const readPct = total > 0 ? Math.round((read / total) * 100) : 0;

  return (
    <div className="mb-5">
      <HighlightBanner gradient="bg-gradient-to-r from-blue-50 via-white to-green-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <FaEnvelope className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-designer-dark tabular-nums">
                {stats.total_messages ?? 0}
              </p>
              <p className="text-xs text-gray-500">Total messages</p>
            </div>
          </div>
          <div className="sm:ml-auto w-full sm:w-auto">
            <ProgressBar
              pct={readPct}
              label={`${readPct}% read`}
              barColor="bg-blue-500"
              trackColor="bg-gray-100"
            />
          </div>
        </div>
      </HighlightBanner>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<FaEnvelope />} label="Total" value={stats.total_messages} accent="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={<FaSpinner />} label="Unread" value={stats.unread_messages} accent="text-yellow-500" bg="bg-yellow-50" />
        <StatCard icon={<FaCheck />} label="Read" value={stats.read_messages} accent="text-green-600" bg="bg-green-50" />
        <StatCard icon={<FaChartLine />} label="Last 7 Days" value={stats.last_7_days} accent="text-blue-600" bg="bg-blue-50" />
      </div>
    </div>
  );
};
